import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchServerConfig, SERVER_CONFIG_SYNC_CHANNEL } from './serverConfigUtils'

const POLL_INTERVAL_MS = 60_000 // 1 minute

type MaintenanceModeState = {
  isLoading: boolean
  maintenanceMode: boolean
  maintenanceStartsAt: string | null
  maintenanceEndsAt: string | null
}

/**
 * Hook that monitors the server's maintenance mode status.
 * - Fetches on mount
 * - Polls every 60 seconds
 * - Listens to BroadcastChannel for cross-tab notifications
 * - When tab becomes visible, re-checks immediately
 *
 * Returns loading state and current maintenance mode status.
 */
export const useMaintenanceMode = (): MaintenanceModeState => {
  const [state, setState] = useState<MaintenanceModeState>({
    isLoading: true,
    maintenanceMode: false,
    maintenanceStartsAt: null,
    maintenanceEndsAt: null,
  })
  const broadcastChannel = useRef<BroadcastChannel | null>(null)
  const previousMaintenanceMode = useRef<boolean | null>(null)

  const checkMaintenanceMode = useCallback(async (): Promise<void> => {
    const serverConfig = await fetchServerConfig()

    if (serverConfig === null) {
      // Server unreachable - keep current state, stop loading
      setState((prev) => ({ ...prev, isLoading: false }))
      return
    }

    // Broadcast to other tabs if maintenance mode changed
    if (previousMaintenanceMode.current !== null && broadcastChannel.current) {
      if (serverConfig.maintenanceMode && !previousMaintenanceMode.current) {
        broadcastChannel.current.postMessage({ type: 'maintenance_mode_enabled' })
      } else if (!serverConfig.maintenanceMode && previousMaintenanceMode.current) {
        broadcastChannel.current.postMessage({ type: 'maintenance_mode_disabled' })
      }
    }
    previousMaintenanceMode.current = serverConfig.maintenanceMode

    setState({
      isLoading: false,
      maintenanceMode: serverConfig.maintenanceMode,
      maintenanceStartsAt: serverConfig.maintenanceStartsAt,
      maintenanceEndsAt: serverConfig.maintenanceEndsAt,
    })
  }, [])

  useEffect(() => {
    // Set up BroadcastChannel for cross-tab communication
    if (typeof BroadcastChannel !== 'undefined') {
      broadcastChannel.current = new BroadcastChannel(SERVER_CONFIG_SYNC_CHANNEL)

      broadcastChannel.current.onmessage = (event) => {
        if (event.data?.type === 'maintenance_mode_enabled') {
          setState((prev) => ({ ...prev, isLoading: false, maintenanceMode: true }))
        } else if (event.data?.type === 'maintenance_mode_disabled') {
          setState((prev) => ({ ...prev, isLoading: false, maintenanceMode: false }))
        }
      }
    }

    // Initial fetch
    void checkMaintenanceMode()

    // Check when tab becomes visible
    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') {
        void checkMaintenanceMode()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Periodic polling
    const intervalId = setInterval(() => {
      void checkMaintenanceMode()
    }, POLL_INTERVAL_MS)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      clearInterval(intervalId)
      if (broadcastChannel.current) {
        broadcastChannel.current.close()
        broadcastChannel.current = null
      }
    }
  }, [checkMaintenanceMode])

  return state
}
